import React, { useEffect, useRef, memo, Component } from 'react';

/**
 * Error boundary — catches TradingView's async script errors when tab switches.
 */
class TVErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(_err) { /* swallow */ }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center text-[#94A3B8] text-sm" style={{ height: this.props.height || 500 }}>
          TradingView widget unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * TradingView Advanced Chart widget embed.
 * Free public embed - no API key needed.
 */
function TradingViewChart({ symbol = 'RELIANCE', exchange = 'NSE', height = 500, interval = 'D' }) {
  const container = useRef(null);
  const scriptRef = useRef(null);

  useEffect(() => {
    if (!container.current) return;

    // Guard against React StrictMode double-invocation & re-renders
    if (scriptRef.current || container.current.querySelector('script')) return;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    // Use .text (safe DOM API) instead of innerHTML — payload is a controlled config object.
    script.text = JSON.stringify({
      autosize: true,
      symbol: `${exchange}:${symbol}`,
      interval,
      timezone: 'Asia/Kolkata',
      theme: 'dark',
      style: '1',
      locale: 'en',
      toolbar_bg: '#0A0B0E',
      enable_publishing: false,
      allow_symbol_change: true,
      hide_side_toolbar: false,
      studies: ['MASimple@tv-basicstudies', 'RSI@tv-basicstudies'],
      support_host: 'https://www.tradingview.com',
    });
    container.current.appendChild(script);
    scriptRef.current = script;

    return () => {
      // Do not manipulate DOM aggressively; TradingView's script polls the DOM async.
      // React will remove the container automatically. Just clear our ref.
      scriptRef.current = null;
    };
  }, [symbol, exchange, interval]);

  return (
    <div
      className="tradingview-widget-container"
      ref={container}
      style={{ height: `${height}px`, width: '100%' }}
      data-testid="tradingview-widget"
    >
      <div className="tradingview-widget-container__widget" style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

const MemoChart = memo(TradingViewChart);

// Wrap with ErrorBoundary + force remount on symbol change via key
export default function TradingViewWidget(props) {
  return (
    <TVErrorBoundary height={props.height}>
      <MemoChart key={`${props.exchange || 'NSE'}:${props.symbol}:${props.interval || 'D'}`} {...props} />
    </TVErrorBoundary>
  );
}
